import React, { useState, useRef } from 'react';
import {
  Box,
  Button,
  Stepper,
  Step,
  StepLabel,
  Typography,
  TextField,
  Alert,
  LinearProgress,
} from '@mui/material';
import { FormattedMessage } from 'react-intl';

import ApiClient from '../../loopback/apiClient';
import GenericDialog from '../GenericDialog';
import ContentBox from '../layout/ContentBox';
import { convertToQid, parseQidToUserId } from '../../utilities/stringHelper';
import { parseBulkImportEmails, parseBulkEnrollQids } from '../utilities/ExcelImport';
import { downloadExcelSheets } from '../utilities/ExcelExport';
import type { EvaluationSheet } from '../utilities/ExcelExport';
import { showNotification, showErrorNotification } from '../../redux/actions';
import { useAppDispatch } from '../../hooks/reduxHooks';
import type { QuipUserWithInstitute } from '../../redux/types';

type Props = {
  isOpen: boolean;
  trialId: number;
  trialName: string;
  closeDialog: () => void;
  onSuccess?: () => void;
};

const STEPS = ['internalTrial.bulkStep1', 'internalTrial.bulkStep2', 'internalTrial.bulkStep3'];

export default function BulkInternalEnrollDialog({
  isOpen,
  trialId,
  trialName,
  closeDialog,
  onSuccess,
}: Props) {
  const dispatch = useAppDispatch();
  const [activeStep, setActiveStep] = useState(0);
  const [matchedList, setMatchedList] = useState<QuipUserWithInstitute[]>([]);
  const [unmatchedEmails, setUnmatchedEmails] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [reuploadFile, setReuploadFile] = useState<File | null>(null);
  const [trialNameInput, setTrialNameInput] = useState('');
  const [bulkResult, setBulkResult] = useState<{
    enrolled: number;
    failed: number;
    skipped?: number;
    errors: { userId: number; error: string }[];
  } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const reuploadInputRef = useRef<HTMLInputElement>(null);

  const resetState = () => {
    setActiveStep(0);
    setMatchedList([]);
    setUnmatchedEmails([]);
    setError(null);
    setLoading(false);
    setReuploadFile(null);
    setTrialNameInput('');
    setBulkResult(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    if (reuploadInputRef.current) {
      reuploadInputRef.current.value = '';
    }
  };

  const handleClose = () => {
    resetState();
    closeDialog();
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) {
      return;
    }
    setError(null);
    setLoading(true);
    try {
      const emails = await parseBulkImportEmails(file);
      if (emails.length === 0) {
        setError('internalTrial.bulkImportNoEmails');
        setLoading(false);
        return;
      }
      const users = await ApiClient.findUsersByEmails(emails);
      const userEmails = new Set(users.map((u) => (u.email || '').toLowerCase()));
      const matched = users;
      const unmatched = emails.filter((email) => !userEmails.has(email.toLowerCase()));
      setMatchedList(matched);
      setUnmatchedEmails(unmatched);
      setActiveStep(1);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'internalTrial.bulkImportError';
      setError(msg);
      dispatch(showErrorNotification(msg));
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = () => {
    const matchedHeader = [
      'Quip Id',
      'Email',
      'Firstname',
      'Surname',
      'Title',
      'Institute',
      'Institute City',
      'Institute Country',
      'Hospital Name',
      'Patient Care',
    ];
    const matchedData = matchedList.map((user) => {
      const institute = user.institute as any;
      const country = institute?.country ?? user.userCountry ?? '';
      const qid = convertToQid(user.id, country);
      const patientCare = institute.isCompany ? 'no' : 'yes';
      return [
        qid,
        user.email || '',
        user.firstname || '',
        user.surname || '',
        user.title || '',
        institute?.name ?? '',
        institute?.city ?? '',
        institute?.country ?? '',
        institute?.hospitalName ?? '',
        patientCare,
      ];
    });
    const unmatchedHeader = ['Email', 'Reason'];
    const unmatchedData = unmatchedEmails.map((email) => [email, 'Not found in system']);
    const sheets: EvaluationSheet = {
      0: { name: 'Matched', data: [matchedHeader, ...matchedData] },
      1: { name: 'Unmatched', data: [unmatchedHeader, ...unmatchedData] },
    };
    const filename = `internal-bulk-${trialId}-${Date.now()}`;
    downloadExcelSheets(sheets, filename);
    dispatch(showNotification(<FormattedMessage id="done" />));
  };

  const handleReuploadChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    setReuploadFile(file || null);
    setError(null);
  };

  const handleRegister = async () => {
    if (trialNameInput !== trialName) {
      setError('internalTrial.bulkTrialNameMismatch');
      dispatch(showErrorNotification('internalTrial.bulkTrialNameMismatch'));
      return;
    }
    if (!reuploadFile) {
      setError('internalTrial.bulkReuploadRequired');
      return;
    }
    setError(null);
    setLoading(true);
    try {
      const userIds = await parseBulkEnrollQids(reuploadFile, parseQidToUserId);
      if (userIds.length === 0) {
        setError('internalTrial.bulkEnrollNoValidQids');
        setLoading(false);
        return;
      }
      const result = await ApiClient.bulkEnrollUsers(trialId, userIds, 'none');
      setBulkResult(result);
      if (result.enrolled > 0) {
        dispatch(showNotification(<FormattedMessage id="done" />));
        onSuccess?.();
      }
      if (result.failed > 0 && result.errors.length) {
        dispatch(showErrorNotification(`internalTrial.bulkEnrollPartial: ${result.failed} failed`));
      }
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'internalTrial.bulkEnrollError';
      setError(msg);
      dispatch(showErrorNotification(msg));
    } finally {
      setLoading(false);
    }
  };

  return (
    <GenericDialog isOpen={isOpen} onRequestClose={handleClose}>
      <ContentBox
        show
        title={<FormattedMessage id="internalTrial.bulkRegister" />}
      >
        <Stepper activeStep={activeStep} sx={{ mb: 2 }}>
          {STEPS.map((labelId) => (
            <Step key={labelId}>
              <StepLabel><FormattedMessage id={labelId} /></StepLabel>
            </Step>
          ))}
        </Stepper>

        {loading && <LinearProgress sx={{ mb: 1 }} />}
        {error && (
          <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 1 }}>
            <FormattedMessage id={error} defaultMessage={error} />
          </Alert>
        )}

        {activeStep === 0 && (
          <Box>
            <Typography sx={{ mb: 1 }}>
              <FormattedMessage id="internalTrial.bulkStep1Description" />
            </Typography>
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,.xlsx,.xls"
              onChange={handleFileSelect}
              style={{ display: 'block', marginTop: 8 }}
            />
            <Typography sx={{ mt: 2, mb: 1 }} variant="body2" color="text.secondary">
              <FormattedMessage id="internalTrial.bulkStep1SkipHint" />
            </Typography>
            <Button
              variant="outlined"
              onClick={() => setActiveStep(2)}
              sx={{ mt: 1 }}
            >
              <FormattedMessage id="internalTrial.bulkSkipToRegister" />
            </Button>
          </Box>
        )}

        {activeStep === 1 && (
          <Box>
            <Typography sx={{ mb: 1 }}>
              <FormattedMessage
                id="internalTrial.bulkSummary"
                values={{ matched: matchedList.length, unmatched: unmatchedEmails.length }}
              />
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
              <Button variant="contained" color="primary" onClick={handleDownload}>
                <FormattedMessage id="internalTrial.bulkDownload" />
              </Button>
              <Button variant="contained" onClick={() => setActiveStep(2)}>
                <FormattedMessage id="internalTrial.bulkNext" />
              </Button>
            </Box>
          </Box>
        )}

        {activeStep === 2 && (
          <Box>
            <Typography sx={{ mb: 1 }}>
              <FormattedMessage id="internalTrial.bulkStep3Description" />
            </Typography>
            <input
              ref={reuploadInputRef}
              type="file"
              accept=".xlsx,.xls"
              onChange={handleReuploadChange}
              style={{ display: 'block', marginTop: 8, marginBottom: 16 }}
            />
            <TextField
              fullWidth
              label={<FormattedMessage id="internalTrial.bulkTrialNameLabel" />}
              value={trialNameInput}
              onChange={(e) => setTrialNameInput(e.target.value)}
              placeholder={trialName}
              sx={{ mb: 2 }}
            />
            {bulkResult && (
              <Alert severity="info" sx={{ mb: 2 }}>
                <FormattedMessage
                  id="internalTrial.bulkEnrollResultWithSkipped"
                  values={{
                    enrolled: bulkResult.enrolled,
                    failed: bulkResult.failed,
                    skipped: bulkResult.skipped ?? 0,
                  }}
                />
              </Alert>
            )}
            <Box
              sx={{
                display: 'flex',
                gap: 1,
                justifyContent: 'space-between',
                flexWrap: 'wrap',
              }}
            >
              <Button
                variant="outlined"
                color="primary"
                onClick={() => setActiveStep(1)}
              >
                <FormattedMessage id="internalTrial.bulkBackToDownload" />
              </Button>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button variant="contained" color="secondary" onClick={handleClose}>
                  <FormattedMessage id="genericClose" />
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleRegister}
                  disabled={!reuploadFile || !trialNameInput || loading}
                >
                  <FormattedMessage id="internalTrial.bulkRegisterConfirm" />
                </Button>
              </Box>
            </Box>
          </Box>
        )}

        {activeStep < 2 && (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
            <Button variant="contained" color="secondary" onClick={handleClose}>
              <FormattedMessage id="genericClose" />
            </Button>
          </Box>
        )}
      </ContentBox>
    </GenericDialog>
  );
}
